// @ts-nocheck
/**
 * パック開封演出画面
 *
 * 開封されたカードを演出付きで表示
 */

import React, { useState, useEffect } from 'react';
import { RARITY_COLORS, RARITY_NAMES } from '../data/constants';
import { valueCalculator } from '../systems/valueCalculator';
import { useIsMobile } from '../hooks/useMediaQuery';
import CardDetail from './CardDetail';
import { CardV2, RarityEffectLayer } from '../../components/cardV2';
import { CARD_DISPLAY_SIZES } from '../../components/cardV2/cardConstants';
import { CARD_BACK } from '../../utils/constants';
import {
  EFFECT_LEVELS,
  RARITY_KEYFRAMES,
  isRareOrAbove,
  getRevealFlashColor,
} from '../../styles/rarityEffects';

// ========================================
// スタイル定義
// ========================================

const createStyles = (isMobile) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(10,10,26,0.98) 0%, rgba(26,26,58,0.98) 50%, rgba(10,10,26,0.98) 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    animation: 'fadeIn 0.5s ease-out',
    padding: isMobile ? '12px' : '0',
    boxSizing: 'border-box',
  },
  title: {
    fontSize: isMobile ? '20px' : '28px',
    fontWeight: 'bold',
    color: '#ffd700',
    marginBottom: isMobile ? '16px' : '32px',
    textShadow: '0 0 20px rgba(255,215,0,0.5)',
    textAlign: 'center',
  },
  cardsContainer: {
    display: 'flex',
    gap: isMobile ? '8px' : '16px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    maxWidth: isMobile ? '100%' : '800px',
    padding: isMobile ? '8px' : '16px',
  },
  cardWrapper: {
    perspective: '1000px',
    cursor: 'pointer',
  },
  cardInner: {
    width: isMobile ? CARD_DISPLAY_SIZES.packMobile.width : CARD_DISPLAY_SIZES.pack.width,
    height: isMobile ? CARD_DISPLAY_SIZES.packMobile.height : CARD_DISPLAY_SIZES.pack.height,
    position: 'relative',
    transformStyle: 'preserve-3d',
    transition: 'transform 0.8s ease-out',
  },
  cardFlipped: {
    transform: 'rotateY(180deg)',
  },
  cardBack: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    borderRadius: isMobile ? '8px' : '12px',
    // 注意: background ショートハンドを使用して個別プロパティとの競合を防ぐ
    background: `url(${CARD_BACK}) center/cover no-repeat`,
    boxShadow: '0 4px 20px rgba(107,76,230,0.5)',
  },
  cardFront: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    transform: 'rotateY(180deg)',
    borderRadius: isMobile ? '8px' : '12px',
    overflow: 'visible',
  },
  controlsContainer: {
    marginTop: isMobile ? '16px' : '32px',
    display: 'flex',
    gap: isMobile ? '8px' : '16px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  button: {
    padding: isMobile ? '10px 20px' : '12px 32px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: isMobile ? '14px' : '16px',
    transition: 'all 0.3s ease',
  },
  revealAllButton: {
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    color: '#fff',
  },
  closeButton: {
    background: 'rgba(107,76,230,0.3)',
    color: '#e0e0e0',
    border: '1px solid rgba(107,76,230,0.5)',
  },
  summaryText: {
    marginTop: isMobile ? '12px' : '16px',
    fontSize: isMobile ? '12px' : '14px',
    color: '#a0a0a0',
    textAlign: 'center',
    flexWrap: 'wrap',
  },
  summaryHighlight: {
    color: '#ffd700',
    fontWeight: 'bold',
  },
});

// ========================================
// キーフレームアニメーション
// ========================================

const keyframeStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes sparkle {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  @keyframes cardAppear {
    0% { transform: scale(0.5) translateY(50px); opacity: 0; }
    100% { transform: scale(1) translateY(0); opacity: 1; }
  }

  @keyframes glowPulse {
    0%, 100% { box-shadow: 0 0 20px rgba(255,215,0,0.3); }
    50% { box-shadow: 0 0 40px rgba(255,215,0,0.6); }
  }

  @keyframes revealFlash {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.9;
      transform: scale(1.3);
    }
    100% {
      opacity: 0;
      transform: scale(2);
    }
  }

  @keyframes rareReveal {
    0% {
      transform: rotateY(180deg) scale(1);
      filter: brightness(1);
    }
    30% {
      filter: brightness(2);
    }
    100% {
      transform: rotateY(180deg) scale(1);
      filter: brightness(1);
    }
  }

  @keyframes screenFlash {
    0% { opacity: 0; }
    20% { opacity: 0.8; }
    100% { opacity: 0; }
  }
`;

// ========================================
// カードコンポーネント（CardV2 + RarityEffectLayer使用）
// ========================================

const PackCard = ({ cardData, index, isFlipped, onFlip, onShowDetail, isNew, onRevealFlash, effectLevel = EFFECT_LEVELS.FULL, isMobile = false }) => {
  const styles = createStyles(isMobile);
  const { card, rarity } = cardData;
  const rarityColor = RARITY_COLORS[rarity] || '#808080';
  const isRare = isRareOrAbove(rarity);
  const [showLocalFlash, setShowLocalFlash] = useState(false);

  // カードサイズ（CardV2のアスペクト比に統一）
  const cardWidth = isMobile ? CARD_DISPLAY_SIZES.packMobile.width : CARD_DISPLAY_SIZES.pack.width;
  const cardHeight = isMobile ? CARD_DISPLAY_SIZES.packMobile.height : CARD_DISPLAY_SIZES.pack.height;

  // フラッシュ色
  const flashColor = getRevealFlashColor(rarity);

  const handleClick = () => {
    if (!isFlipped) {
      // めくる時にフラッシュ演出
      if (isRare && flashColor && effectLevel === EFFECT_LEVELS.FULL) {
        setShowLocalFlash(true);
        onRevealFlash?.(flashColor);
        setTimeout(() => setShowLocalFlash(false), 500);
      }
      onFlip(index);
    } else {
      onShowDetail(cardData);
    }
  };

  return (
    <div
      style={{
        ...styles.cardWrapper,
        animation: `cardAppear 0.5s ease-out ${index * 0.1}s both`,
      }}
      onClick={handleClick}
    >
      <div style={{
        ...styles.cardInner,
        ...(isFlipped ? {
          ...styles.cardFlipped,
          animation: isRare && effectLevel === EFFECT_LEVELS.FULL ? 'rareReveal 0.6s ease-out' : undefined,
        } : {}),
      }}>
        {/* カード裏面 */}
        <div style={styles.cardBack} />

        {/* カード表面 */}
        <div style={styles.cardFront}>
          {/* ローカルフラッシュ（最前面） */}
          {showLocalFlash && flashColor && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: flashColor,
              borderRadius: isMobile ? 8 : 12,
              animation: 'revealFlash 0.5s ease-out forwards',
              zIndex: 20,
              pointerEvents: 'none',
            }} />
          )}

          {/* レアリティバッジ（オーバーレイ） */}
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: isMobile ? '2px 4px' : '3px 8px',
            borderRadius: 4,
            fontSize: isMobile ? 9 : 11,
            fontWeight: 'bold',
            color: '#fff',
            background: rarityColor,
            zIndex: 15,
          }}>
            {rarity}
          </div>

          {/* NEWバッジ */}
          {isNew && (
            <div style={{
              position: 'absolute',
              top: -8,
              left: -8,
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)',
              color: '#fff',
              fontSize: isMobile ? 8 : 10,
              fontWeight: 'bold',
              padding: '2px 6px',
              borderRadius: 8,
              zIndex: 15,
              boxShadow: '0 2px 8px rgba(255,75,117,0.5)',
            }}>
              NEW
            </div>
          )}

          {/* RarityEffectLayer + CardV2（isFlipped時のみ描画） */}
          {isFlipped && (
            <RarityEffectLayer
              rarity={rarity}
              effectLevel={effectLevel}
              width={cardWidth}
              height={cardHeight}
              enableMouseEffect={true}
            >
              <CardV2
                card={card}
                displayMode="pack"
                displayWidth={cardWidth}
              />
            </RarityEffectLayer>
          )}

          {/* 未めくり時のプレースホルダー（RarityEffectLayerなしでCardV2表示） */}
          {!isFlipped && (
            <CardV2
              card={card}
              displayMode="pack"
              displayWidth={cardWidth}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================

const PackOpening = ({
  cards,
  onClose,
  existingCollection = [],
  effectLevel = EFFECT_LEVELS.FULL,
  packCount = 1,
  packInfo = null, // { type: 'standard' | 'attribute' | 'royal', attribute?: string }
}) => {
  const isMobile = useIsMobile();
  const styles = createStyles(isMobile);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [allRevealed, setAllRevealed] = useState(false);
  const [selectedCardForDetail, setSelectedCardForDetail] = useState(null);
  const [screenFlash, setScreenFlash] = useState(null);

  // カード詳細を表示
  const handleShowDetail = (cardData) => {
    setSelectedCardForDetail(cardData);
  };

  // カード詳細を閉じる
  const handleCloseDetail = () => {
    setSelectedCardForDetail(null);
  };

  // スクリーンフラッシュ
  const handleRevealFlash = (color) => {
    if (effectLevel !== EFFECT_LEVELS.FULL) return;
    setScreenFlash(color);
    setTimeout(() => setScreenFlash(null), 400);
  };

  // スタイルシートを追加
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = keyframeStyles + RARITY_KEYFRAMES;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  // カードが新規かどうかを判定
  const isNewCard = (cardId, rarity) => {
    // 既存コレクションに同じカード・レアリティがあるか確認
    return !existingCollection.some(
      c => c.cardId === cardId && c.rarity === rarity && c.quantity > 0
    );
  };

  // カードをめくる
  const flipCard = (index) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      newSet.add(index);

      // 全カードがめくられたかチェック
      if (newSet.size === cards.length) {
        setAllRevealed(true);
      }

      return newSet;
    });
  };

  // 全てめくる
  const revealAll = () => {
    const allIndices = new Set(cards.map((_, i) => i));
    setFlippedCards(allIndices);
    setAllRevealed(true);
  };

  // サマリー計算
  const getSummary = () => {
    const rarityCounts = {};
    let newCardCount = 0;

    cards.forEach(({ cardId, rarity }) => {
      rarityCounts[rarity] = (rarityCounts[rarity] || 0) + 1;
      if (isNewCard(cardId, rarity)) {
        newCardCount++;
      }
    });

    return { rarityCounts, newCardCount };
  };

  const summary = getSummary();

  return (
    <div style={styles.overlay}>
      {/* スクリーンフラッシュ（レア出現時） */}
      {screenFlash && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: screenFlash,
          animation: 'screenFlash 0.4s ease-out forwards',
          pointerEvents: 'none',
          zIndex: 3000,
        }} />
      )}

      {/* タイトル */}
      <div style={{
        ...styles.title,
        ...(packInfo?.type === 'royal' ? { color: '#ffd700', textShadow: '0 0 30px rgba(255,215,0,0.7)' } : {}),
      }}>
        {packInfo?.type === 'royal'
          ? '👑 ロイヤルパック開封！'
          : packInfo?.type === 'attribute'
            ? `${packInfo.attribute}属性パック開封！`
            : packCount > 1
              ? `${packCount}パック開封！`
              : 'パック開封！'}
      </div>

      {/* カード枚数表示 */}
      {packCount > 1 && (
        <div style={{
          fontSize: '14px',
          color: '#a0a0a0',
          marginBottom: '16px',
          marginTop: '-20px',
        }}>
          {cards.length}枚のカード
        </div>
      )}

      {/* カード表示 */}
      <div style={{
        ...styles.cardsContainer,
        // 多数のカードの場合はスクロール可能に
        ...(cards.length > 10 ? {
          maxHeight: isMobile ? '50vh' : '60vh',
          overflowY: 'auto',
          padding: isMobile ? '8px' : '16px',
        } : {}),
        // カードが多い場合は小さめのギャップ
        ...(cards.length > 15 ? { gap: isMobile ? '6px' : '10px' } : {}),
      }}>
        {cards.map((cardData, index) => (
          <PackCard
            key={`${cardData.cardId}_${cardData.rarity}_${index}`}
            cardData={cardData}
            index={index}
            isFlipped={flippedCards.has(index)}
            onFlip={flipCard}
            onShowDetail={handleShowDetail}
            isNew={isNewCard(cardData.cardId, cardData.rarity)}
            onRevealFlash={handleRevealFlash}
            effectLevel={effectLevel}
            isMobile={isMobile}
          />
        ))}
      </div>

      {/* サマリー表示（全カードめくり後） */}
      {allRevealed && (
        <div style={styles.summaryText}>
          獲得:
          {Object.entries(summary.rarityCounts)
            .sort((a, b) => {
              const order = ['GR', 'SP', 'ALT', 'SEC', 'HR', 'UR', 'SR', 'R', 'UC', 'C'];
              return order.indexOf(a[0]) - order.indexOf(b[0]);
            })
            .map(([rarity, count]) => (
              <span key={rarity} style={{
                color: RARITY_COLORS[rarity] || '#e0e0e0',
                marginLeft: '8px',
              }}>
                {rarity}×{count}
              </span>
            ))
          }
          {summary.newCardCount > 0 && (
            <span style={{ marginLeft: '16px' }}>
              (<span style={styles.summaryHighlight}>NEW: {summary.newCardCount}枚</span>)
            </span>
          )}
        </div>
      )}

      {/* コントロールボタン */}
      <div style={styles.controlsContainer}>
        {!allRevealed && (
          <button
            style={{
              ...styles.button,
              ...styles.revealAllButton,
            }}
            onClick={revealAll}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 15px rgba(107,76,230,0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'none';
              e.target.style.boxShadow = 'none';
            }}
          >
            全てめくる
          </button>
        )}

        <button
          style={{
            ...styles.button,
            ...styles.closeButton,
          }}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(107,76,230,0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(107,76,230,0.3)';
          }}
        >
          {allRevealed ? '閉じる' : 'スキップして閉じる'}
        </button>
      </div>

      {/* カード詳細モーダル（売却なし） */}
      {selectedCardForDetail && (
        <CardDetail
          card={selectedCardForDetail.card}
          rarity={selectedCardForDetail.rarity}
          quantity={1}
          valueInfo={valueCalculator.calculateCardValue(selectedCardForDetail.card)}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
};

export default PackOpening;
